import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import Input from "Components/UI/Input";
import { useGetEmployeeQuery } from "AppQuries/employee/employee_list";
import { useSelector } from "react-redux";
import { loggedInUser } from "core/redux/slice/authSlice";
import { useFormattedUserListOption } from "core/Operations/formattedOption/useFormattedMasterOptions";
import dayjs from "dayjs";
import { format } from "date-fns";

// const payDateOfMonth = 1;

const isStatusOption = [
  { id: 1, label: "Yes", value: true },
  { id: 0, label: "No", value: false },
];

const financeTypeOptions = [
  { id: 1, label: "Advance", value: "ADVANCE" },
  { id: 2, label: "Loan", value: "LOAN" },
];

const payDateOptions = Array.from({ length: 30 }, (_, i) => ({
  id: i + 1,
  label: (i + 1).toString(),
  value: i + 1,
}));
const AdvancePaymentForm = () => {
  const userData = useSelector(loggedInUser);

  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const isApproved = watch("isApproved");

  const { data: employees } = useGetEmployeeQuery({
    companyCode: userData?.companyCode,
    locationCode: userData?.locationCode,
  });

  const userListOptions = useFormattedUserListOption({
    companyCode: userData?.companyCode,
    locationCode: userData?.locationCode,
  });

  const employeeOptions =
    employees
      ?.filter((d) => d.isActive)
      ?.map((o) => ({
        id: o.employeeCode,
        label: `${o.uniqueId}-${o.fullName}`,
        value: o.employeeCode,
      })) || [];

  const tenure = watch("tenure");
  const type = watch("type");
  const amount = watch("advanceAmount");
  const startDate = watch("repaymentStartDate");
  const endDate = watch("repaymentEndDate");
  const payDateOfMonth = watch("payDateOfMonth");

  useEffect(() => {
    if (type === "LOAN" && amount && tenure) {
      const emi = Math.ceil(amount / tenure);
      setValue("emi", emi);
    } else {
      setValue("emi", 0);
    }
  }, [type, amount, tenure]);

  useEffect(() => {
    if (type === "LOAN" && startDate && endDate) {
      const months = dayjs(endDate).diff(dayjs(startDate), "month") + 1;

      setValue("tenure", months);
    }
  }, [startDate, endDate]);

  const interestRate = watch("interestRate");

  useEffect(() => {
    if (type === "LOAN" && amount && tenure) {
      const emi = calculateEMI(amount, interestRate || 0, tenure);
      setValue("emi", emi);
    }
  }, [type, amount, tenure, interestRate]);

  const calculateEMI = (P, annualRate, n) => {
    const r = annualRate / 12 / 100;

    if (!r) return Math.ceil(P / n);

    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

    return Math.ceil(emi);
  };

  const getRepaymentStartDate = (payDateOfMonth) => {
    const today = dayjs();

    let startDate = today.date(payDateOfMonth);

    if (today.date() > payDateOfMonth) {
      startDate = startDate.add(1, "month");
    }

    return startDate;
  };

  const getRepaymentEndDate = (startDate, tenure) => {
    return startDate.add(tenure - 1, "month");
  };

  // useEffect(() => {
  //   if (type === "LOAN" && tenure && payDateOfMonth) {
  //     const start = getRepaymentStartDate(payDateOfMonth);
  //     const end = getRepaymentEndDate(start, tenure);

  //     setValue("repaymentStartDate", start.format("YYYY-MM-DD"));
  //     setValue("repaymentEndDate", end.format("YYYY-MM-DD"));
  //   }
  // }, [tenure, payDateOfMonth, type]);

  useEffect(() => {
    if (type !== "LOAN") return;
    if (!tenure || !payDateOfMonth) return;

    const today = dayjs();

    let startDate = today.date(payDateOfMonth);

    if (today.date() > payDateOfMonth) {
      startDate = startDate.add(1, "month");
    }

    const daysInMonth = startDate.daysInMonth();
    startDate = startDate.date(Math.min(payDateOfMonth, daysInMonth));

    const endDate = startDate.add(tenure - 1, "month");

    setValue("repaymentStartDate", startDate.format("YYYY-MM-DD"));
    setValue("repaymentEndDate", endDate.format("YYYY-MM-DD"));
  }, [tenure, payDateOfMonth, type]);

  return (
    <div className="form-row row">
      {/* Employee */}
      <div className="col-md-4 mb-3">
        <Input
          name="employeeCode"
          type="commonSelect"
          label="Employee"
          optionData={employeeOptions}
          className={`form-control ${errors.employeeCode ? "is-invalid" : ""}`}
          required
          onChange={(val) => setValue("employeeCode", val)}
          defaultValue={watch("employeeCode")}
        />
      </div>

      <div className="col-md-4 mb-3">
        <Input
          name="type"
          type="commonSelect"
          label="Type"
          optionData={financeTypeOptions}
          className={`form-control ${errors.type ? "is-invalid" : ""}`}
          required
          onChange={(val) => {
            setValue("type", val);
            trigger("type");
          }}
          defaultValue={watch("type")}
        />
      </div>

      {/* Amount */}
      <div className="col-md-4 mb-3">
        <Input
          name="advanceAmount"
          type="number" // ✅ FIXED
          label="Amount"
          className={`form-control ${errors.advanceAmount ? "is-invalid" : ""}`}
          required
        />
      </div>

      {type === "LOAN" && (
        <div className="col-md-4 mb-3">
          <Input
            name="interestRate"
            type="number"
            label="Interest Rate (%)"
            className={`form-control ${errors.interestRate ? "is-invalid" : ""}`}
            required
            onChange={(e) => {
              setValue("interestRate", e.target.value);
              trigger("interestRate");
            }}
          />
        </div>
      )}

      {type === "LOAN" && (
        <div className="col-md-4 mb-3">
          <Input
            name="tenure"
            type="number"
            label="Tenure (Months)"
            className={`form-control ${errors.tenure ? "is-invalid" : ""}`}
            required={type === "LOAN"}
            onChange={(e) => {
              setValue("tenure", e.target.value);
              trigger("tenure");
            }}
          />
        </div>
      )}

      {type === "LOAN" && (
        <div className="col-md-4 mb-3">
          <Input
            name="emi"
            type="number"
            label="EMI"
            className="form-control"
            disabled
          />
        </div>
      )}

      {/* Advance Date */}
      {/* <div className="col-md-4 mb-3">
        <Input
          name="advanceDate"
          type="date"
          label="Date"
          className={`form-control ${errors.advanceDate ? "is-invalid" : ""}`}
          required
          onChange={(date, str) => {
            setValue("advanceDate", str); // ✅ FIXED
            trigger("advanceDate");
          }}
          disabledDate={(current) =>
            current < dayjs().subtract(1, "month").endOf("month")
          }
        />
      </div> */}

      <div className="col-md-4 mb-3">
        <Input
          name="payDateOfMonth"
          type="commonSelect"
          label="Salary Pay Date"
          optionData={payDateOptions}
          className={`form-control ${errors.payDateOfMonth ? "is-invalid" : ""}`}
          required
          onChange={(val) => {
            setValue("payDateOfMonth", val);
            trigger("payDateOfMonth");
          }}
          defaultValue={watch("payDateOfMonth")}
        />
      </div>

      {/* <div className="col-md-4 mb-3">
        <Input
          name="payDateOfMonth"
          type="text"
          label="Salary Pay Date (Day)"
          //  labelClassName="form-label"
          className={`form-control ${errors.payDateOfMonth ? "is-invalid" : ""}`}
          // placeholder="e.g. 25"
          min={1}
          max={31}
          required
          onChange={(e) => {
            setValue("payDateOfMonth", e.target.value);
            trigger("payDateOfMonth");
          }}
        />
      </div> */}

      <div className="col-md-4 col-sg-12 mb-3">
        <Input
          name="advanceDate"
          type="date"
          placeholder="Date"
          className={`form-control ${errors.advanceDate ? "is-invalid" : ""}`}
          label="Advance Date"
          //  labelClassName="form-label"
          required={true}
          requiredMessage="Advance date is required"
          defaultValue=""
          dateFormat="YYYY-MM-DD"
          onChange={(date, dateString) => {
            setValue("advanceDate", dateString);
            trigger("advanceDate");
          }}
          // disabledDate={(current) => {
          //   return current < dayjs().subtract(1, "month").endOf("month");
          // }}
          // allowClear={false}
        />
      </div>

      {/* Repayment Start */}
      <div className="col-md-4 mb-3">
        <Input
          name="repaymentStartDate"
          type="date"
          label="Repayment Start Date"
          className={`form-control ${
            errors.repaymentStartDate ? "is-invalid" : ""
          }`}
          required
          onChange={(date, str) => {
            setValue("repaymentStartDate", str);
            trigger("repaymentStartDate");
          }}
          defaultValue={
            watch("repaymentStartDate")
              ? format(new Date(watch("repaymentStartDate")), "yyyy-MM-dd")
              : ""
          }
          allowClear={false}
          dateFormat="YYYY-MM-DD"
        />
      </div>

      {/* Repayment End */}
      <div className="col-md-4 mb-3">
        <Input
          name="repaymentEndDate"
          type="date"
          label="Repayment End Date"
          className={`form-control ${
            errors.repaymentEndDate ? "is-invalid" : ""
          }`}
          required
          onChange={(date, str) => {
            setValue("repaymentEndDate", str);
            trigger("repaymentEndDate");
          }}
          defaultValue={
            watch("repaymentEndDate")
              ? format(new Date(watch("repaymentEndDate")), "yyyy-MM-dd")
              : ""
          }
          allowClear={false}
          dateFormat="YYYY-MM-DD"
        />
      </div>

      {/* Reason */}
      <div className="col-md-4 mb-3">
        <Input
          name="reason"
          type="text"
          label="Reason"
          className={`form-control ${errors.reason ? "is-invalid" : ""}`}
          required
        />
      </div>

      {/* Is Approved */}
      <div className="col-md-4 mb-3">
        <Input
          name="isApproved"
          type="commonSelect"
          label="Is Approved"
          optionData={isStatusOption}
          className={`form-control ${
            errors.isApproved ? "is-invalid" : "" // ✅ FIXED
          }`}
          required
          onChange={(val) => {
            setValue("isApproved", val);
            trigger("isApproved");
          }}
          defaultValue={watch("isApproved")}
        />
      </div>

      {/* Approved By */}
      <div className="col-md-4 mb-3">
        <Input
          name="approvedBy"
          type="commonSelect"
          label="Approved By"
          optionData={userListOptions}
          className={`form-control ${errors.approvedBy ? "is-invalid" : ""}`}
          required={isApproved === true} // ✅ FIXED
          onChange={(val) => {
            setValue("approvedBy", val);
            trigger("approvedBy");
          }}
        />
      </div>

      {/* Approval Date */}
      <div className="col-md-4 mb-3">
        <Input
          name="approvalDate"
          type="date"
          label="Approval Date"
          className={`form-control ${errors.approvalDate ? "is-invalid" : ""}`}
          required={isApproved === true} // ✅ FIXED
          onChange={(date, str) => {
            setValue("approvalDate", str); // ✅ FIXED
            trigger("approvalDate");
          }}
          dateFormat="YYYY-MM-DD"
          allowClear={false}
        />
      </div>

      {/* Status */}
      <div className="col-md-4 mb-3">
        <Input
          name="isActive"
          type="commonSelect"
          label="Status"
          optionData={isStatusOption}
          className={`form-control ${errors.isActive ? "is-invalid" : ""}`}
          required
          onChange={(val) => {
            setValue("isActive", val);
            trigger("isActive");
          }}
          defaultValue={watch("isActive")}
        />
      </div>
    </div>
  );
};

export default AdvancePaymentForm;

// import React from "react";
// import { useFormContext } from "react-hook-form";
// import Input from "Components/UI/Input";
// import { useGetEmployeeQuery } from "AppQuries/employee/employee_list";
// import { useSelector } from "react-redux";
// import { loggedInUser } from "core/redux/slice/authSlice";
// import { useFormattedUserListOption } from "core/Operations/formattedOption/useFormattedMasterOptions";
// import dayjs from "dayjs";

// const isStatusOption = [
//   { id: 1, label: "Yes", value: true },
//   { id: 0, label: "No", value: false },
// ];

// const AdvancePaymentForm = () => {
//   const userData = useSelector(loggedInUser);
//   const {
//     control,
//     register,
//     watch,
//     setValue,
//     trigger,
//     formState: { errors },
//   } = useFormContext();

//   const { data: employees } = useGetEmployeeQuery({
//     companyCode: userData?.companyCode,
//     locationCode: userData?.locationCode,
//   });

//   const userListOptions = useFormattedUserListOption({
//     companyCode: userData?.companyCode,
//     locationCode: userData?.locationCode,
//   });

//   const employeeOptions = Array.isArray(employees)
//     ? employees
//         .filter((d) => d.isActive)
//         .map((o) => ({
//           id: o.employeeCode,
//           label: `${o.uniqueId}-${o.fullName}`,
//           value: o.employeeCode.toString(),
//         }))
//     : [];

//   return (
//     <div className="form-row row">
//       <div className="col-md-4 col-sg-12 mb-3">
//         <Input
//           name="employeeCode"
//           type="commonSelect"
//           placeholder="Select Employee"
//           className={`form-control select ${
//             errors.employeeCode ? "is-invalid" : ""
//           }`}
//           label="Employee"
//          //  labelClassName="form-label"
//           optionData={employeeOptions}
//           required={true}
//           requiredMessage="Employee is required"
//           onChange={(e) => {
//             setValue("employeeCode", e);
//           }}
//           defaultValue={watch("employeeCode")}
//         />
//       </div>

//       <div className="col-md-4 col-sg-12 mb-3">
//         <Input
//           name="advanceDate"
//           type="date"
//           placeholder="Date"
//           className={`form-control ${errors.advanceDate ? "is-invalid" : ""}`}
//           label="Advance Date"
//          //  labelClassName="form-label"
//           required={true}
//           requiredMessage="Advance date is required"
//           defaultValue=""
//           dateFormat="YYYY-MM-DD"
//           onChange={(date, dateString) => {
//             setValue("advanceDate", dateString);
//             trigger("advanceDate");
//           }}
//           disabledDate={(current) => {
//             return current < dayjs().subtract(1, "month").endOf("month");
//           }}
//         />
//       </div>

//       <div className="col-md-4 col-sg-12 mb-3">
//         <Input
//           name="advanceAmount"
//           type="text"
//           placeholder="Advance Amount"
//           className={`form-control ${errors.advanceAmount ? "is-invalid" : ""}`}
//           label="Advance Amount"
//          //  labelClassName="form-label"
//           required={true}
//           requiredMessage="Advance Amount is required"
//           defaultValue=""
//         />
//       </div>

//       <div className="col-md-4 col-sg-12 mb-3">
//         <Input
//           name="reason"
//           type="text"
//           placeholder="Reason "
//           className={`form-control ${errors.reason ? "is-invalid" : ""}`}
//           label="Reason"
//          //  labelClassName="form-label"
//           required={true}
//           requiredMessage="Reason is required"
//           defaultValue=""
//         />
//       </div>

//       <div className="col-md-4 col-sg-12 mb-3">
//         <Input
//           name="repaymentStartDate"
//           type="date"
//           placeholder="Repayment Start Date"
//           className={`form-control ${
//             errors.repaymentStartDate ? "is-invalid" : ""
//           }`}
//           label="Repayment Start Date"
//          //  labelClassName="form-label"
//           required={true}
//           requiredMessage="Repayment Start Date is required"
//           defaultValue=""
//           dateFormat="YYYY-MM-DD"
//           onChange={(date, dateString) => {
//             setValue("repaymentStartDate", dateString);
//             trigger("repaymentStartDate");
//           }}
//           // disabledDate={(current) => {
//           //   return current < dayjs().subtract(1, "day").endOf("day");
//           // }}
//         />
//       </div>

//       <div className="col-md-4 col-sg-12 mb-3">
//         <Input
//           name="repaymentEndDate"
//           type="date"
//           placeholder="Repayment End Date"
//           className={`form-control ${
//             errors.repaymentEndDate ? "is-invalid" : ""
//           }`}
//           label="Repayment End Date"
//          //  labelClassName="form-label"
//           required={true}
//           requiredMessage="Repayment End Date is required"
//           defaultValue=""
//           dateFormat="YYYY-MM-DD"
//           onChange={(date, dateString) => {
//             setValue("repaymentStartDate", dateString);
//             trigger("repaymentStartDate");
//           }}
//           getPopupContainer={(trigger) => trigger.parentElement}
//           // disabledDate={(current) => {
//           //   return current < dayjs().subtract(1, "day").endOf("day");
//           // }}
//         />
//       </div>

//       <div className="col-md-4 col-sg-12 mb-3">
//         <Input
//           name="isApproved"
//           type="commonSelect"
//           placeholder="Select Is Approved"
//           className={`form-control select ${
//             errors.isApproval ? "is-invalid" : ""
//           }`}
//           label="Is Approved"
//          //  labelClassName="form-label"
//           optionData={isStatusOption}
//           required={true}
//           requiredMessage="Is Approved is required"
//           onChange={(selectedOption) => {
//             setValue("isApproved", selectedOption);
//             trigger("isApproved");
//           }}
//           defaultValue={watch("isApproved")}
//         />
//       </div>

//       <div className="col-md-4 col-sg-12 mb-3">
//         <Input
//           name="approvedBy"
//           type="commonSelect"
//           placeholder="Approved By"
//           className={`form-control ${errors.approvedBy ? "is-invalid" : ""}`}
//           optionData={userListOptions}
//           label="Approved By"
//          //  labelClassName="form-label"
//           required={watch("approvedBy") ? true : false}
//           requiredMessage="Approved By is required"
//           onChange={(selectedOption) => {
//             setValue("approvedBy", selectedOption);
//             trigger("approvedBy");
//           }}
//           defaultValue=""
//         />
//       </div>

//       <div className="col-md-4 col-sg-12 mb-3">
//         <Input
//           name="approvalDate"
//           type="date"
//           placeholder="Approval Date"
//           className={`form-control ${errors.approvalDate ? "is-invalid" : ""}`}
//           label="Approval Date"
//          //  labelClassName="form-label"
//           required={watch("isApproved") ? true : false}
//           requiredMessage="Approval date is required"
//           defaultValue=""
//           dateFormat="YYYY-MM-DD"
//           onChange={(date, dateString) => {
//             setValue("repaymentStartDate", dateString);
//             trigger("repaymentStartDate");
//           }}
//           disabledDate={(current) => {
//             return current < dayjs().subtract(1, "month").endOf("month");
//           }}
//         />
//       </div>

//       <div className="col-md-4 col-sg-12 mb-3">
//         <Input
//           name="isActive"
//           type="commonSelect"
//           placeholder="Select Status"
//           className={`form-control select ${
//             errors.isActive ? "is-invalid" : ""
//           }`}
//           label="Status"
//          //  labelClassName="form-label"
//           optionData={isStatusOption}
//           required={true}
//           requiredMessage="Status is required"
//           onChange={(selectedOption) => {
//             setValue("isActive", selectedOption);
//             trigger("isActive");
//           }}
//           defaultValue={watch("isActive")}
//         />
//       </div>
//     </div>
//   );
// };

// export default AdvancePaymentForm;
